// import { EMPTY_FORM_DATA } from './DataService'; // Removed unused import
import { allSections } from '../components/InputSection/InputSection'; // Assuming this path is correct
import { generateClientToken } from './ServerlessAuth';

/**
 * Constants for supported file types
 */
export const SUPPORTED_MIME_TYPES = {
  LINKEDIN_ZIP: 'application/zip',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // Add PDF later if needed
  PDF: 'application/pdf',
};

export const SUPPORTED_EXTENSIONS = {
  LINKEDIN_ZIP: '.zip',
  DOCX: '.docx',
  PDF: '.pdf',
};

// Serverless parser configuration - Updated for Vercel deployment
// Environment variables for .env file:
// - REACT_APP_PARSER_API_URL: Custom parser API URL (defaults to 'https://privacv-parser.vercel.app')
// - REACT_APP_REQUIRE_PARSER_TOKENS: 'true' to enable client authentication (default: false)
// - REACT_APP_PARSER_API_KEY: Public API key identifier (safe for client-side, default: 'privacv-web-client')
// - REACT_APP_ALLOWED_ORIGINS: Comma-separated list of additional allowed origins
const PARSER_API_BASE_URL = process.env.REACT_APP_PARSER_API_URL || 'https://privacv-parser.vercel.app';
const REQUIRE_TOKENS = process.env.REACT_APP_REQUIRE_PARSER_TOKENS === 'true';

/**
 * Parse CV file using serverless parser
 * @param {File} file - The CV file to parse (PDF or DOCX)
 * @param {Object} options - Optional configuration
 * @returns {Promise<Object>} - Parsed CV data in PrivaCV format
 */
export const parseCV = async (file, options = {}) => {
  const {
    onProgress = () => {},
    timeout = 120000, // 120 seconds for Vercel serverless processing (includes cold start)
    maxFileSize = 50 * 1024 * 1024 // 50MB
  } = options;

  // Validate file
  if (!file) {
    throw new Error('No file provided');
  }

  if (file.size > maxFileSize) {
    throw new Error(`File too large. Maximum size: ${maxFileSize / (1024 * 1024)}MB`);
  }

  const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|docx)$/i)) {
    throw new Error('Unsupported file type. Please upload a PDF or DOCX file.');
  }

  console.log(`Parsing CV using Vercel serverless: ${PARSER_API_BASE_URL}`);
  onProgress({ status: 'uploading', message: 'Uploading file to Vercel parser...' });

  const formData = new FormData();
  formData.append('file', file);

  try {
    // Generate client authentication token if required
    const origin = window.location.origin;
    const headers = {
      'Origin': origin,
      'X-Client-Origin': origin,
      'X-Request-Source': 'privacv-web'
    };
    
    if (REQUIRE_TOKENS) {
      const clientToken = await generateClientToken(origin);
      headers['X-Client-Token'] = clientToken;
      headers['Authorization'] = `Client ${clientToken}`;
    }

    const response = await fetch(`${PARSER_API_BASE_URL}/api/parse-cv`, {
      method: 'POST',
      body: formData,
      headers,
      signal: AbortSignal.timeout(timeout)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Parser failed: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorData.message || errorData.detail || errorMessage;
      } catch {
        // Use default error message if JSON parsing fails
        if (errorText) {
          errorMessage = `Parser failed: ${errorText.substring(0, 200)}`;
        }
      }
      
      throw new Error(errorMessage);
    }

    onProgress({ status: 'parsing', message: 'Processing CV data...' });

    const result = await response.json();
    
    onProgress({ 
      status: 'completed', 
      message: 'CV parsed successfully!',
      confidence: result.confidence || result.score
    });

    return {
      formData: result.formData || result.data,
      populatedSectionKeys: result.populatedSectionKeys || result.sections || [],
      confidence: result.confidence || result.score,
      metadata: result.metadata || result.meta
    };

  } catch (error) {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      throw new Error('Request timed out. Vercel serverless functions may take longer on cold starts. Please try again.');
    }
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Unable to connect to Vercel parser service. Please check your internet connection and try again.');
    }

    // Handle Vercel-specific error responses
    if (error.message.includes('502') || error.message.includes('Bad Gateway')) {
      throw new Error('Vercel function temporarily unavailable. Please try again in a moment.');
    }

    if (error.message.includes('504') || error.message.includes('Gateway Timeout')) {
      throw new Error('Vercel function timed out. The file might be too large or complex to process.');
    }
    
    throw error;
  }
};

/**
 * Legacy support - redirect to serverless parser
 */
export const parsePdf = async (file, options) => {
  console.log('parsePdf: Using serverless parser');
  return parseCV(file, options);
};

export const parseDocx = async (file, options) => {
  console.log('parseDocx: Using serverless parser');
  return parseCV(file, options);
};

export const parseLinkedInZip = async (file, options = {}) => {
  // Try serverless LinkedIn parsing first, fall back to client-side if not supported
  try {
    console.log('parseLinkedInZip: Attempting serverless LinkedIn parser');
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'linkedin');

    const origin = window.location.origin;
    const headers = { 'Origin': origin };
    
         if (REQUIRE_TOKENS) {
       const clientToken = await generateClientToken(origin);
       headers['X-Client-Token'] = clientToken;
       headers['Authorization'] = `Client ${clientToken}`;
     }

    const response = await fetch(`${PARSER_API_BASE_URL}/api/parse-linkedin`, {
      method: 'POST',
      body: formData,
      headers,
      signal: AbortSignal.timeout(options.timeout || 60000)
    });

    if (response.ok) {
      const result = await response.json();
      return {
        formData: result.formData || result.data,
        populatedSectionKeys: result.populatedSectionKeys || result.sections || [],
        confidence: result.confidence || result.score,
        metadata: result.metadata || result.meta
      };
    } else {
      throw new Error(`Serverless LinkedIn parsing failed: ${response.status}`);
    }
  } catch (error) {
    console.log('parseLinkedInZip: Serverless parsing failed, falling back to client-side');
    // Fall back to existing client-side LinkedIn parsing
    const { parseLinkedInZip: clientSideParser } = await import('./linkedin/LinkedInParser');
    return clientSideParser(file, options);
  }
};

/**
 * Health check for the parser service
 * @returns {Promise<boolean>} - Whether the service is healthy
 */
export const checkParserHealth = async () => {
  try {
    const response = await fetch(`${PARSER_API_BASE_URL}/api/health`, {
      method: 'GET',
      timeout: 5000
    });
    
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get parser service information
 * @returns {Promise<Object>} - Service information
 */
export const getParserInfo = async () => {
  try {
    const response = await fetch(`${PARSER_API_BASE_URL}/api/info`, {
      method: 'GET',
      timeout: 5000
    });
    
    if (response.ok) {
      return await response.json();
    }
    
    throw new Error(`Failed to get parser info: ${response.statusText}`);
  } catch (error) {
    console.error('Failed to get parser info:', error);
    throw error;
  }
};

/**
 * Test connectivity to the Vercel serverless parser
 * @returns {Promise<Object>} - Connectivity test results
 */
export const testParserConnectivity = async () => {
  const results = {
    baseUrl: PARSER_API_BASE_URL,
    health: false,
    info: null,
    error: null,
    timestamp: new Date().toISOString()
  };

  try {
    // Test health endpoint
    const healthResponse = await fetch(`${PARSER_API_BASE_URL}/api/health`, {
      method: 'GET',
      timeout: 10000
    });
    results.health = healthResponse.ok;

    if (healthResponse.ok) {
      // Try to get service info
      try {
        const infoResponse = await fetch(`${PARSER_API_BASE_URL}/api/info`, {
          method: 'GET',
          timeout: 10000
        });
        
        if (infoResponse.ok) {
          results.info = await infoResponse.json();
        }
      } catch (infoError) {
        console.warn('Info endpoint failed:', infoError);
      }
    }

  } catch (error) {
    results.error = error.message;
    console.error('Parser connectivity test failed:', error);
  }

  return results;
};

/**
 * Reads the content of a file based on the specified type.
 * @param {File} file - The file object to read.
 * @param {'arrayBuffer' | 'text'} readAs - How to read the file content.
 * @returns {Promise<ArrayBuffer | string>} A promise resolving with the file content.
 */
export const readFileContent = (file, readAs) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      resolve(event.target.result);
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      reject(new Error(`Error reading file: ${error.message || error}`));
    };

    switch (readAs) {
      case 'arrayBuffer':
        reader.readAsArrayBuffer(file);
        break;
      case 'text':
        reader.readAsText(file);
        break;
      default:
        reject(new Error(`Unsupported readAs type: ${readAs}`));
    }
  });
};

/**
 * Updates the application context with parsed data.
 * Handles replacing existing data and selecting populated sections.
 *
 * @param {object} parsedResult - The result from a parser function.
 * @param {object} parsedResult.formData - The partial or full formData object.
 * @param {string[]} parsedResult.populatedSectionKeys - Keys of sections that received data.
 * @param {object} contextSetters - Object containing context update functions.
 * @param {Function} contextSetters.updateFormData - Function to update FormContext.
 * @param {Function} contextSetters.setSelectedSections - Function to update selected sections.
 * @param {Function} [contextSetters.setFullCustomColorScheme] - Optional: Function to update custom colors if relevant.
 * @param {Function} [contextSetters.setSelectedLayout] - Optional: Function to update layout if relevant.
 * @param {Function} [contextSetters.setSelectedDesign] - Optional: Function to update design if relevant.
 * @param {Function} [contextSetters.setSelectedStyle] - Optional: Function to update style if relevant.
 * @param {Function} [contextSetters.setSelectedBackground] - Optional: Function to update background if relevant.
 * @param {Function} [contextSetters.updateTypographySettings] - Optional: Function to update typography if relevant.
 */
export const updateAppContext = (parsedResult, contextSetters) => {
  const { formData: importedFormData, populatedSectionKeys = [] } = parsedResult;
  const { updateFormData, setSelectedSections } = contextSetters;

  if (!updateFormData || !setSelectedSections) {
    console.error('ImportService.updateAppContext: Missing required context setters (updateFormData, setSelectedSections).');
    return false; // Return false if setters are missing
  }

  // --- Import Strategy: Replace ---
  const confirmReplace = window.confirm(
    'Importing data will replace your current resume content in the populated sections. Are you sure you want to continue?'
  );

  if (!confirmReplace) {
    console.log('Import cancelled by user.');
    return false; // Return false if user cancelled
  }

  console.log('ImportService: Applying imported form data:', importedFormData);
  updateFormData(null, importedFormData);

  if (populatedSectionKeys.length > 0) {
    const finalSections = allSections
        .filter(section => populatedSectionKeys.includes(section.sectionKey))
        .map(section => ({ ...section, expanded: true }));

    console.log('ImportService: Auto-selecting populated sections:', finalSections.map(s => s.name));
    setSelectedSections(finalSections);
  } else {
    console.log('ImportService: No specific sections were populated by the import.');
  }

  console.log('ImportService: Application context updated.');
  return true; // Return true on successful application
}; 